// Auth service for API calls
// Task 2.3: Unified authentication patterns using apiClient consistently

import apiClient from '../../utils/apiClient.js';
import AuthError from '../../utils/AuthError.js';

// Login user
// Implements user story: "As a user, when I log in I must receive a usable JWT refresh token string"
const login = async (userData) => {
  try {
    // Use apiClient.post instead of direct fetch for consistency
    // This ensures all API calls follow the same authentication pattern
    const response = await apiClient.post('auth/login', userData);
    
    // Handle both standardized and legacy response formats for backward compatibility
    // Standardized format: { data: { user, accessToken, refreshToken } }
    // Legacy format: { user, accessToken, refreshToken }
    const responseData = response.data.data || response.data;
    
    // Check if user needs to change password
    if (responseData.mustChangePassword) {
      // Return password change requirement without storing tokens
      return {
        mustChangePassword: true,
        userId: responseData.userId,
        username: responseData.username,
        message: responseData.message
      };
    }
    
    // Save user and tokens to localStorage for normal login
    // Store both access and refresh JWT tokens for unified token management
    localStorage.setItem('user', JSON.stringify(responseData.user));
    localStorage.setItem('token', responseData.accessToken);
    localStorage.setItem('refreshToken', responseData.refreshToken);
    
    // Return data in the same format as before to maintain backward compatibility
    return responseData;
  } catch (error) {
    console.error('Login service error:', error.message);
    
    // Standardize error parsing: catch apiClient errors and throw uniform AuthError
    // This provides consistent error format: { message, status }
    const status = error.status || 401;
    throw new AuthError(error.message || 'Login failed', status);
  }
};

// Logout user
// Use apiClient for consistent API communication instead of direct fetch
const logout = async (refreshToken) => {
  // Remove user data from localStorage first
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  
  // If we have a refresh token, call logout endpoint using apiClient
  if (refreshToken) {
    try {
      // Use apiClient.post for consistency with other API calls
      await apiClient.post('auth/logout', { refreshToken });
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, but local cleanup completed:', error.message);
    }
  }
  
  return { success: true };
};

// Refresh token
// Replace direct fetch with apiClient.post for unified authentication pattern
const refreshToken = async (refreshToken) => {
  try {
    // Use apiClient.post instead of fetch for consistency
    // Backend endpoint is '/auth/refresh-token' as per routes/auth.js
    const response = await apiClient.post('auth/refresh-token', { refreshToken });
    
    // Handle both standardized and legacy response formats for backward compatibility
    // Standardized format: { data: { accessToken, refreshToken } }
    // Legacy format: { accessToken, refreshToken }
    const responseData = response.data.data || response.data;
    
    // Update stored tokens using the new JWT refresh token format
    localStorage.setItem('token', responseData.accessToken);
    if (responseData.refreshToken) {
      localStorage.setItem('refreshToken', responseData.refreshToken);
    }
    
    return responseData;
  } catch (error) {
    // Clear tokens on refresh failure and throw standardized AuthError
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Provide consistent error format for the calling code
    const status = error.status || 401;
    throw new AuthError(error.message || 'Failed to refresh token', status);
  }
};

// Check current auth state
const checkAuthState = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  
  // Auth state logging - Removed for production
  
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        // Token is expired
      } else if (payload.exp) {
        // Token is valid
      }
    } catch {
      // Failed to decode token
    }
  }
  
  const authState = {
    isAuthenticated: !!token,
    user,
    token,
  };
  
  return authState;
};

// Get current user profile 
// Implements user story interface requirement: getCurrentUser(): Promise<UserProfile>
const getCurrentUser = async () => {
  try {
    // Use apiClient.get for consistent API communication
    const response = await apiClient.get('users/me');
    
    // Handle both standardized and legacy response formats for backward compatibility
    // Standardized format: { data: { user } }
    // Legacy format: { user } or direct user object
    const responseData = response.data.data || response.data;
    
    // Update stored user data to keep it fresh
    localStorage.setItem('user', JSON.stringify(responseData));
    
    return responseData;
  } catch (error) {
    // Throw standardized AuthError for consistent error handling
    const status = error.status || 401;
    throw new AuthError(error.message || 'Failed to get current user', status);
  }
};

// Change password on first login
const changePasswordFirstLogin = async (userId, tempPassword, newPassword) => {
  try {
    const response = await apiClient.post('auth/change-password-first-login', {
      userId,
      tempPassword,
      newPassword
    });
    
    // Handle response format - the API returns tokens on successful password change
    const responseData = response.data.data || response.data;
    
    // Don't automatically store tokens - force user to login again manually
    // This prevents infinite redirect loops between password change and login
    // Clear any existing auth data to ensure clean state
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    return responseData;
  } catch (error) {
    console.error('Password change service error:', error.message);
    throw new AuthError(error.message || 'Failed to change password', error.status || 400);
  }
};

// Request password reset
const requestPasswordReset = async (email) => {
  try {
    const response = await apiClient.post('auth/forgot-password', { email });
    
    // Handle response format
    const responseData = response.data.data || response.data;
    
    return responseData;
  } catch (error) {
    console.error('Request password reset service error:', error.message);
    throw new AuthError(error.message || 'Failed to request password reset', error.status || 400);
  }
};

// Reset password with token
const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiClient.post('auth/reset-password', {
      token,
      newPassword
    });
    
    // Handle response format
    const responseData = response.data.data || response.data;
    
    return responseData;
  } catch (error) {
    console.error('Reset password service error:', error.message);
    throw new AuthError(error.message || 'Failed to reset password', error.status || 400);
  }
};

const authService = {
  login,
  logout,
  refreshToken,
  checkAuthState,
  getCurrentUser,  // Added to maintain interface requirement: getCurrentUser(): Promise<UserProfile>
  changePasswordFirstLogin,
  requestPasswordReset,
  resetPassword,
};

export default authService;
